import React, { useMemo } from 'react';
import { CollapsibleSection } from '@/components/core/application-search/components/CollapsableSection';

import s from './FullSearchResults.module.scss';
import { ContentLoader } from '@/components/core/application-search/components/ContentLoader';
import { SearchResultsSection } from '@/components/core/application-search/components/SearchResultsSection';
import { useFullApplicationSearch } from '@/services/search/hooks/useFullApplicationSearch';
import { FoundItem, SearchResult } from '@/services/search/types';
import { SearchCategories } from '@/components/core/application-search/components/SearchCategories';
import { NothingFound } from '@/components/core/application-search/components/NothingFound';

interface Props {
  searchTerm: string;
  onTryAiSearch: () => void;
  onAskAi?: (item: FoundItem) => void;
  onClose?: () => void;
  activeCategory: keyof SearchResult | null;
  setActiveCategory: (category: keyof SearchResult | null) => void;
  mode?: 'regular' | 'ai';
  onToggleMode?: (mode: 'regular' | 'ai') => void;
}

export const FullSearchResults = ({
  searchTerm,
  onTryAiSearch,
  onAskAi,
  onClose,
  activeCategory,
  setActiveCategory,
  mode,
  onToggleMode,
}: Props) => {
  const { data, isLoading } = useFullApplicationSearch(searchTerm);

  const totalFound =
    (data?.teams?.length ?? 0) +
    (data?.events?.length ?? 0) +
    (data?.members?.length ?? 0) +
    (data?.projects?.length ?? 0) +
    (data?.forumThreads?.length ?? 0);

  const sortedData = useMemo(() => {
    if (!data) {
      return [];
    }

    const sortedByKey = Object.keys(data)
      .map((key) => {
        return {
          key,
          length: data[key as keyof SearchResult]?.length ?? 0,
        };
      })
      .sort((a, b) => {
        if (a.key === 'top') {
          return -1;
        }

        if (a.length > b.length) {
          return -1;
        }

        if (a.length < b.length) {
          return 1;
        }

        return 0;
      });

    return sortedByKey
      .map((item) => {
        const values = data[item.key as keyof SearchResult];
        return {
          label: getLabel(item.key),
          disabled: !values?.length,
          values,
          key: item.key,
        };
      })
      .filter(Boolean);
  }, [data]);

  function renderContent() {
    if (isLoading) {
      return <ContentLoader />;
    }

    if (!totalFound && searchTerm) {
      return <NothingFound onClick={onTryAiSearch} searchTerm={searchTerm} />;
    }

    return sortedData.map((item) => {
      if (!item.values?.length) {
        return null;
      }

      if (activeCategory && item.key !== activeCategory) {
        return null;
      }

      return (
        <CollapsibleSection
          key={item.label}
          title={`${item.label} (${item.values?.length ?? 0})`}
          disabled={!item.values?.length}
          // initialOpen={(item.label === 'Top Results' && !!item.values?.length) || activeCategory === item.key}
          initialOpen
          // forceOpen={activeCategory === item.key}
          forceOpen
          hideControl
        >
          <SearchResultsSection
            groupItems={item.key === 'top'}
            items={item.values ?? []}
            query={searchTerm}
            onSelect={onClose}
            onAskAi={onAskAi}
          />
        </CollapsibleSection>
      );
    });
  }

  return (
    <div className={s.root}>
      <div className={s.totalFoundLabel}>Total results ({totalFound})</div>
      <div className={s.sticky}>
        <SearchCategories
          data={data}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          mode={mode}
          onToggleMode={onToggleMode}
        />
      </div>
      {renderContent()}
    </div>
  );
};

function getLabel(key: string) {
  if (key === 'top') {
    return 'Top Results';
  }

  if (key === 'forumThreads') {
    return 'Forum';
  }

  return key;
}
